import { useProjectParams } from '@hooks/useProjectParams';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ErrorHandlingModal } from 'components/ErrorHandlingModal';
import { ButtonWithTooltip } from 'components/WithTooltip';
import { useProjectData } from 'hooks/useProjectData';
import { Button } from 'uikit';

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRemoveWorkOrderChange } from '../hooks';

export const WorkOrderChangeRemove = ({
    isDisabledWithTooltip,
    isDisabled,
    styles,
    refreshProject,
    isOpenedOuter,
    workOrderChangeId,
    closeOuter,
}: {
    isDisabled?: boolean;
    isDisabledWithTooltip?: boolean;
    styles?: CSSProperties;
    refreshProject: () => void;
    isOpenedOuter?: boolean;
    workOrderChangeId?: number;
    closeOuter?: () => void;
}) => {
    const { id: projectId, orderChangeId, methodology } = useProjectParams();

    const { projectData } = useProjectData(projectId, false);

    const currentWorkOrderChangeId = useMemo(
        () => Number(orderChangeId) || workOrderChangeId,
        [orderChangeId, workOrderChangeId],
    );

    const navigate = useNavigate();
    const {
        confirmationDialogOpened,
        openConfirmationDialogHandler,
        submitDeleteHandler,
        cancelDeleteHandler,
        hasError,
        deleteWorkOrderChangeData,
        deleteWorkOrderChangeStatus,
        deleteWorkOrderChangeErr,
        closeErrorModalHandler,
        createErrorReportHandler,
    } = useRemoveWorkOrderChange({ refreshProject });

    const removeBtnStyles = useMemo(
        () =>
            isDisabledWithTooltip || isDisabled
                ? { ...styles }
                : { ...styles, color: 'var(--color-typo-alert)', borderColor: 'var(--color-typo-alert)' },
        [isDisabled, isDisabledWithTooltip, styles],
    );

    useEffect(() => {
        if (isOpenedOuter && currentWorkOrderChangeId) {
            openConfirmationDialogHandler(currentWorkOrderChangeId);
        }
    }, [currentWorkOrderChangeId, isOpenedOuter, openConfirmationDialogHandler, workOrderChangeId]);

    const onOpenDialog = useCallback(() => {
        openConfirmationDialogHandler(currentWorkOrderChangeId);
    }, [openConfirmationDialogHandler, currentWorkOrderChangeId]);

    const onSubmit = useCallback(() => {
        submitDeleteHandler?.();
    }, [submitDeleteHandler]);

    const onCloseConfirmationModal = useCallback(
        (isSubmit: boolean) => {
            if (!isSubmit) {
                cancelDeleteHandler();
                if (isOpenedOuter) {
                    closeOuter?.();
                }
            }
        },
        [cancelDeleteHandler, closeOuter, isOpenedOuter],
    );

    useEffect(() => {
        if (
            deleteWorkOrderChangeData?.uerDeleteWorkOrderChange?.result &&
            deleteWorkOrderChangeData?.uerDeleteWorkOrderChange?.id === currentWorkOrderChangeId
        ) {
            onCloseConfirmationModal(false);

            if (orderChangeId) {
                const currentWorkOrderId = projectData?.contractNavigation?.data?.workOrderNavs?.find(
                    (workOrder) =>
                        workOrder?.workOrderChanges?.some(
                            (workOrderChange) => workOrderChange.id === currentWorkOrderChangeId,
                        ),
                )?.id;

                if (currentWorkOrderId) {
                    navigate(`/projects/${methodology}/${projectId}/order/${currentWorkOrderId}`);
                } else {
                    navigate(`/projects/${methodology}/${projectId}`);
                }
            }
        }
    }, [
        closeOuter,
        currentWorkOrderChangeId,
        deleteWorkOrderChangeData?.uerDeleteWorkOrderChange?.id,
        deleteWorkOrderChangeData?.uerDeleteWorkOrderChange?.result,
        isOpenedOuter,
        methodology,
        navigate,
        onCloseConfirmationModal,
        orderChangeId,
        projectData?.contractNavigation?.data?.workOrderNavs,
        projectId,
    ]);

    return (
        <>
            {!isOpenedOuter &&
                (isDisabledWithTooltip ? (
                    <ButtonWithTooltip
                        buttonProps={{
                            label: 'Удалить',
                            view: 'secondary',
                            size: 's',
                            style: removeBtnStyles,
                        }}
                        tooltipProps={{
                            mode: 'mouseover',
                            content: 'Удаление возможно только в статусе "На формировании"',
                        }}
                        fakeDisabled
                    />
                ) : (
                    <Button
                        label="Удалить"
                        view="secondary"
                        size="s"
                        onClick={onOpenDialog}
                        disabled={isDisabled}
                        style={removeBtnStyles}
                    />
                ))}
            {confirmationDialogOpened && (
                <ConfirmationDialog
                    isOpened={confirmationDialogOpened}
                    onSubmit={onSubmit}
                    onClose={onCloseConfirmationModal}
                    textHeader="Удаление изм. к НЗ"
                    textConfirmation="Вы уверены, что хотите удалить изм. к НЗ?"
                    cancelButtonText="Не удалять"
                    confirmButtonText="Удалить"
                />
            )}
            <ErrorHandlingModal
                titleText="Удаление изм. к НЗ"
                isSubmitted={hasError}
                statusRequest={deleteWorkOrderChangeStatus}
                dataRequest={deleteWorkOrderChangeData?.uerDeleteWorkOrderChange}
                errorsRequest={deleteWorkOrderChangeErr}
                handleCloseModalError={closeErrorModalHandler}
                handleCreateReport={createErrorReportHandler}
            />
        </>
    );
};

WorkOrderChangeRemove.displayName = 'WorkOrderChangeRemove';
